import React from 'react';

import {
  FieldRoot,
  FieldLabel,
  RequiredMark,
  TextareaWrapper,
  StyledTextarea,
} from './styles';

/**
 * Textarea — campo de texto multilinha genérico do design system.
 *
 * Irmão do `Input`: mesma API de rótulo (`label`/`required`), mesmo container
 * com foco ao clicar. Container é um retângulo arredondado (não pill).
 *
 * Acessibilidade: passe `label` (associado via htmlFor) ou `aria-label`.
 *
 * @example
 * <Textarea label="Comentário" placeholder="Deixe seu comentário" rows={4} fullWidth />
 */
export type TextareaProps =
  React.TextareaHTMLAttributes<HTMLTextAreaElement> & {
    /** Ocupa 100% da largura disponível */
    fullWidth?: boolean;
    /** Classe no container externo */
    className?: string;
    /** Rótulo visível acima do campo (associado via htmlFor). */
    label?: React.ReactNode;
    /** Marca como obrigatório: asterisco no rótulo + aria-required. */
    required?: boolean;
    /** Destaca a borda em vermelho (validação). @default false */
    error?: boolean;
  };

let _uid = 0;

export const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      fullWidth = false,
      disabled = false,
      className,
      label,
      required = false,
      error = false,
      rows = 4,
      id,
      ...rest
    },
    ref
  ) => {
    const generatedId = React.useRef(`textarea-${(_uid += 1)}`).current;
    const textareaId = id ?? (label ? generatedId : undefined);

    const field = (
      <TextareaWrapper
        $disabled={disabled}
        $fullWidth={fullWidth}
        $error={error}
        className={label ? undefined : className}
      >
        <StyledTextarea
          ref={ref}
          id={textareaId}
          rows={rows}
          disabled={disabled}
          aria-required={required || undefined}
          aria-invalid={error || undefined}
          {...rest}
        />
      </TextareaWrapper>
    );

    if (!label) return field;

    return (
      <FieldRoot $fullWidth={fullWidth} className={className}>
        <FieldLabel htmlFor={textareaId}>
          {label}
          {required && <RequiredMark aria-hidden="true"> *</RequiredMark>}
        </FieldLabel>
        {field}
      </FieldRoot>
    );
  }
);

export default Textarea;
